﻿import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { MarketplaceResultType } from "config";
import GuidSchema from "_shared/guids/guid-schema";
import Company from "marketplace/companies/company";
import CompanySchema from "marketplace/companies/company-schema";
import type { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import IndividualProfileSchema from "marketplace/individuals/profiles/individual-profile-schema";
import { z } from "zod";

export type IndividualJSON = z.infer<typeof IndividualSchema.IndividualJSONZ>;
export type IndividualParams = z.infer<typeof IndividualSchema.IndividualZ>;
export type IndividualConstructorParams = z.infer<typeof IndividualSchema.IndividualConstructorZ>;

export default class IndividualSchema {
  public static readonly IndividualJSONZ = z.object({
    ...IndividualProfileSchema.IndividualProfileJSONZ.shape,
    id: z.uuid(),
    userId: z.uuid(),
    legalEntityId: z.uuid(),
    isVisible: z.boolean(),
    hasSigningAuthority: z.boolean().nullish(),
    companyId: z.uuid().nullish(),
    companyName: z.string().nullish(),
  });

  public static readonly IndividualZ = z.object({
    id: z.instanceof(Guid),
    userId: z.instanceof(Guid),
    legalEntityId: z.instanceof(Guid),
    profile: IndividualProfileSchema.IndividualProfileZ,
    company: z.instanceof(Company).optional(),
    isVisible: z.boolean(),
    hasSigningAuthority: z.boolean().optional(),
  });

  private static readonly IndividualBaseCtorZ = z.object({
    id: z.instanceof(Guid),
    userId: z.instanceof(Guid),
    isVisible: z.boolean().optional(),
    hasSigningAuthority: z.boolean().optional(),
  });

  private static readonly IndividualOrgInfoZ = z.union([
    z.object({
      company: CompanySchema.CompanyZ,
    }),
    z.object({
      legalEntityId: z.instanceof(Guid),
      entityName: z.string().optional(),
    }),
  ]);

  private static readonly IndividualProfileInputZ = z.union([
    z.object({
      profile: IndividualProfileSchema.IndividualProfileZ,
    }),
    z.object({
      firstName: z.string(),
      lastName: z.string(),
    }),
  ]);

  public static readonly IndividualConstructorZ = IndividualSchema.IndividualBaseCtorZ.and(
    IndividualSchema.IndividualOrgInfoZ,
  ).and(IndividualSchema.IndividualProfileInputZ);

  public static validate(values: unknown): IndividualParams {
    return parseOrThrow(IndividualSchema.IndividualZ, values, {
      logError: true,
      schemaName: "IndividualSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): IndividualJSON {
    return parseOrThrow(IndividualSchema.IndividualJSONZ, json, {
      logError: true,
      schemaName: "IndividualSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static validateJSONArray(jsonArray: unknown): IndividualJSON[] {
    return parseOrThrow(z.array(IndividualSchema.IndividualJSONZ), jsonArray, {
      logError: true,
      schemaName: "IndividualSchema",
      operation: "validateJSONArray",
      payloadSample: jsonArray,
    });
  }

  public static isValid(values: unknown): values is IndividualParams {
    try {
      IndividualSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is IndividualJSON {
    try {
      IndividualSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: IndividualJSON): IndividualParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(IndividualSchema.IndividualJSONZ, json, {
      logError: true,
      schemaName: "IndividualSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return IndividualSchema.codec.decode(parsed);
  }

  public static serialize(values: IndividualParams): IndividualJSON {
    if (isNullOrUndefined(values)) return values;
    const parsed = IndividualSchema.validate(values);
    if (isNullOrUndefined(parsed)) return parsed;
    return IndividualSchema.codec.encode(parsed);
  }

  public static fromMarketplaceSearchInfo(result: MarketplaceSearchInfoJSON): IndividualJSON | undefined {
    if (result.type !== MarketplaceResultType.Individual) {
      return;
    }

    const parsed = IndividualSchema.IndividualJSONZ.safeParse({
      ...result,
      id: result.id,
      userId: result.userId,
      legalEntityId: result.legalEntityId,
      firstName: result.firstName,
      lastName: result.lastName,
      isVisible: result.isVisible ?? false,
    });

    return parsed.success ? parsed.data : undefined;
  }

  private static readonly codec = z.codec(IndividualSchema.IndividualJSONZ, IndividualSchema.IndividualZ, {
    decode: (data) => ({
      id: new Guid(GuidSchema.deserialize(data.id)),
      userId: new Guid(GuidSchema.deserialize(data.userId)),
      legalEntityId: new Guid(GuidSchema.deserialize(data.legalEntityId)),
      profile: IndividualProfileSchema.deserialize(data),
      company: data.companyId
        ? Company.create({
            id: new Guid(GuidSchema.deserialize(data.companyId)),
            legalEntityId: new Guid(GuidSchema.deserialize(data.legalEntityId)),
            profile: CompanyProfile.create({
              name: data.companyName ?? "",
            }),
          })
        : undefined,
      isVisible: data.isVisible,
      hasSigningAuthority: data.hasSigningAuthority ?? false,
    }),
    encode: (individual) => ({
      ...IndividualProfileSchema.serialize(individual.profile),
      id: individual.id.toJSON(),
      userId: individual.userId.toJSON(),
      legalEntityId: individual.legalEntityId.toJSON(),
      companyId: individual.company?.id.toJSON(),
      companyName: individual.company?.profile?.name,
      isVisible: individual.isVisible,
      hasSigningAuthority: individual.hasSigningAuthority ?? false,
    }),
  });
}
